/**
 * subject-loaded.guard.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */


import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanDeactivate,
  Resolve,
  Router
} from "@angular/router";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { Observable } from "rxjs/Rx";

import { Group } from "../auth/identity/group";
import { UserService } from "../auth/identity/user.service";
import { AlertService } from "../common/alert.service";
import { SaveableForm } from "../intake-form/saveable-form";
import { Subject } from "../subjects/subject";
import { SubjectService } from "../subjects/subject.service";
import { ModalHelpers } from "../support/utilities/modals";
import { WatchlistService } from "../watchlist/watchlist.service";

/**
 * A conjunction type for routes with ID values in their parameters.
 */
interface IdParametered {
  params : {id : string};
}

/**
 * A {@link SubjectLoaderGuard} guards routes that require a loaded subject
 * context to operate.
 */
@Injectable()
export class SubjectLoadedGuard implements CanActivate, Resolve<Subject>
{
  /**
   * Create the {@link SubjectLoaderGuard}
   * @param  {SubjectService} subjects
   * @param  {WatchlistService} watchlist
   * @param  {AlertService}   alerts
   * @return {SubjectLoaderGuard}
   */
  constructor (
    protected subjects : SubjectService,
    protected watchlist : WatchlistService,
    protected alerts : AlertService
  )
  {
    // console.log("SubjectLoader args", Array.from(arguments));
  }

  /**
   * Determine whether a route snapshot is {@link IdParametered}. Note the
   * return type is a boolean "type predicate" which can signal to TS about
   * the argument's type in subsequent code branches.
   * @param  {ActivatedRouteSnapshot} rt
   * @return {Boolean}
   */
  protected hasParamId (rt : ActivatedRouteSnapshot)
    : rt is ActivatedRouteSnapshot & IdParametered
  {
    return ("id" in rt.params);
  }

  public canActivate (route : ActivatedRouteSnapshot) : boolean
  {
    if (this.hasParamId(route))
    {
      const subj : Subject = this.subjects.byServerId(+route.params.id);
      if (subj && subj.loaded)
      {
        return true;
      }
      else
      {
        this.alerts.error(`Could not find a loaded subject ${route.params.id}`);
        return false;
      }
    }
    else
    {
      this.alerts.error("Cannot open subject—ID missing or unreadable");
      return false;
    }
  }

  public resolve (route : ActivatedRouteSnapshot) : Subject
  {
    if (!this.hasParamId(route))
    {
      // As long as we always apply this class as CanActivate AND Resolve, this
      // should never happen (i.e. it's a severe static misconfiguration).
      throw new Error("ID parameter missing in resolve guard.");
    }
    return this.subjects.byServerId(+route.params.id);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/routing/subject-loaded.guard.ts